// open-modal.ts — imperativer, aktions-orientierter Modal-Helper (PFS-584).
//
// Duenne Fassade ueber <pfs-modal>: baut das Element, haengt den Content ein,
// setzt die actions (die das Modal IN SEINER SHADOW rendert + verdrahtet) und
// resolved mit dem Ergebnis. Jede Aktion liefert ein DialogResult
// (close(value) | keepOpen(status?)); async-onClick laeuft IM Dialog (Button
// busy), enabled/visible sind reaktive, nutzergetriebene Praedikate.
//
//   const saved = await openModal<Data | null>({
//     title: "Praxis anlegen", size: "md", content: formNode, dismissValue: null,
//     actions: [
//       { label: "Abbrechen", variant: "ghost", onClick: () => DialogResult.close(null) },
//       { label: "Speichern", variant: "success",
//         enabled: () => formNode.checkValidity(),
//         onClick: async () => {
//           try { await api.save(read(formNode)); return DialogResult.close(read(formNode)); }
//           catch { return DialogResult.keepOpen({ text: "Speichern fehlgeschlagen", tone: "error" }); }
//         } },
//     ],
//   });
//
// mount: standardmaessig document.body. Fuer Cut-App-Formulare, deren Felder in
// der App-Shadow gestylt sind, kann eine ShadowRoot uebergeben werden, damit der
// geslottete Content dort korrekt stylt (die Buttons stylt das Modal ohnehin
// selbst).
import "./pfs-modal.js";
import type { ModalAction, PfsModalElement } from "./pfs-modal.js";

export { DialogResult } from "./pfs-modal.js";
export type { ModalAction, ModalStatus, ModalTone } from "./pfs-modal.js";

export interface OpenModalOptions<R> {
  title?: string;
  size?: "sm" | "md" | "lg";
  content: string | Node;
  actions: ModalAction<R>[];
  dismissValue?: R; // Ergebnis bei Backdrop/Escape/x
  dismissible?: boolean; // default true; false -> nur via Aktion schliessbar
  mount?: Element | ShadowRoot; // default document.body
}

export function openModal<R>(opts: OpenModalOptions<R>): Promise<R> {
  return new Promise<R>((resolve) => {
    const modal = document.createElement("pfs-modal") as PfsModalElement;
    if (opts.size) modal.setAttribute("data-size", opts.size);
    if (opts.title) modal.setAttribute("data-title", opts.title);
    if (opts.dismissible === false) modal.setAttribute("data-static", "");

    if (typeof opts.content === "string") {
      const wrap = document.createElement("div");
      wrap.innerHTML = opts.content;
      modal.appendChild(wrap);
    } else {
      modal.appendChild(opts.content);
    }

    let settled = false;
    modal.addEventListener("pfs:modal-close", (e) => {
      if (settled) return;
      settled = true;
      const detail = (e as CustomEvent<{ reason: string; value: unknown }>).detail;
      const value = detail.reason === "action" ? (detail.value as R) : (opts.dismissValue as R);
      resolve(value);
      modal.remove();
    });

    (opts.mount ?? document.body).appendChild(modal);
    modal.setActions(opts.actions as ModalAction<unknown>[]);
    modal.setAttribute("data-open", "");
  });
}
